import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useArchitectApp } from '../hooks/useArchitectApp.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useWorkflow, useWorkflowStatus } from '../contexts/WorkflowContext.jsx';
import Navigation from '../components/Navigation.jsx';
import Footer from '../components/Footer.jsx';
import CreditTracker from '../components/CreditTracker.jsx';
import QueueIndicator from '../components/QueueIndicator.jsx';
import SessionManager from '../components/SessionManager.jsx';
import EnhancedAnalysisLoader from '../components/EnhancedAnalysisLoader.jsx';
import VisualOptionGroup from '../components/VisualOptionGroup.jsx';
import PathSelector from '../components/PathSelector.jsx';
import CloudflareDeployWizard from '../components/CloudflareDeployWizard.jsx';
import {
    Button,
    Card,
    Textarea,
    Text,
    Heading,
    Container,
    FormGroup,
    Icon
} from '../components/ui/index.jsx';

export default function Home() {
    const navigate = useNavigate();
    const { user, isAuthenticated, authType, getAuthInfo } = useAuth();
    const { setWorkflowPageActive, clearWorkflowPageActive } = useWorkflow();
    // Get real-time progress directly from context (not through useArchitectApp which has sync delay)
    const workflowStatus = useWorkflowStatus();

    // Check if user is actually authenticated (not just a guest)
    const isReallyAuthenticated = isAuthenticated && authType === 'authenticated';

    // Path selection: 'blueprint' (prompts) or 'deploy' (Cloudflare)
    // Deploy path only available for authenticated users
    const [selectedPath, setSelectedPath] = useState('blueprint');
    const [infraStatus, setInfraStatus] = useState({ ready: false, github: null, cloudflare: null });

    // Reset to blueprint if user logs out while on deploy/vibe path
    useEffect(() => {
        if (!isReallyAuthenticated && (selectedPath === 'deploy' || selectedPath === 'vibe')) {
            setSelectedPath('blueprint');
        }
    }, [isReallyAuthenticated, selectedPath]);

    const [formData, setFormData] = useState({
        appIdea: '',
        selectedPlatform: 'cursor',
        enableWebSearch: true,
        // 4 Question Answers (Smart Defaults Applied)
        deviceUsage: ['web'],
        appAudience: 'public',
        authMethod: 'email-password',
        developmentStage: 'mvp', // Always use MVP (stage selection removed from UI)
        // Cloudflare-specific
        cfTier: 'free' // 'free' or 'paid' - only used when platform = cloudflare
    });
    const [isLoading, setIsLoading] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);

    // Use the comprehensive app hook
    const {
        initializeSession,
        startWorkflowAsync,
        cancelWorkflow,
        isWorkflowMode,
        workflowState,
        currentStep,
        loadingMessage,
        loadingTitle,
        v2Data,
        isLoading: appIsLoading,
        showAnalysisResults,
        resumeDetectedWorkflow,
        resumeWorkflowData,
        sessionId,
        // Queue system
        queueStatus,
        cancelQueue
    } = useArchitectApp();

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleOptionChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Track previous workflow state to detect completion transition
    const prevWorkflowActiveRef = React.useRef(false);

    // Auto-navigate to results when workflow completes (ONLY on transition from active to complete)
    // This prevents infinite redirect loops when returning to Home after viewing results
    useEffect(() => {
        const wasActive = prevWorkflowActiveRef.current;
        const isNowComplete = workflowStatus.isComplete;
        const isNowActive = workflowStatus.isActive;

        // Update ref for next render
        prevWorkflowActiveRef.current = isNowActive;

        // Only navigate if workflow was active and JUST completed (transition)
        // Don't navigate if isComplete was already true on mount (stale state)
        if (wasActive && isNowComplete && !isNowActive) {
            // Include sessionId in URL - Analyze page needs it to load the session
            const currentSessionId = sessionId || sessionStorage.getItem('currentSessionId');
            navigate(`/analyze?session=${currentSessionId}&tab=overview&skipinit=1`);
        }
    }, [workflowStatus.isComplete, workflowStatus.isActive, navigate, sessionId]);

    // Tell context that Home page is showing inline workflow UI (hide GlobalWorkflowIndicator)
    useEffect(() => {
        // Track when either local or context state shows active workflow
        const showingWorkflowUI = isWorkflowMode || workflowStatus.isActive;
        if (showingWorkflowUI) {
            setWorkflowPageActive('/');
        }
        return () => {
            clearWorkflowPageActive('/');
        };
    }, [isWorkflowMode, workflowStatus.isActive, setWorkflowPageActive, clearWorkflowPageActive]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.appIdea.trim()) {
            window.showToast?.('Please enter your app idea', 'error');
            return;
        }

        if (!formData.selectedPlatform) {
            window.showToast?.('Please select a development platform', 'error');
            return;
        }

        if (formData.deviceUsage.length === 0) {
            window.showToast?.('Please select where people will use your app', 'error');
            return;
        }

        if (!formData.appAudience) {
            window.showToast?.('Please select who this app is for', 'error');
            return;
        }

        if (!formData.authMethod) {
            window.showToast?.('Please select how people should log in', 'error');
            return;
        }

        if (!isReallyAuthenticated) {
            window.showToast?.('Please sign in with Google to continue', 'error');
            return;
        }

        setIsLoading(true);

        try {
            // Helper to map IDs (same as before)
            const getAnswerLabel = (questionId, selectedValues) => {
                // Simplified mapping logic for brevity - can expand if needed
                return Array.isArray(selectedValues) ? selectedValues.join(', ') : selectedValues;
            };

            const answers = [
                { questionId: 'device_usage', question: 'Where do you want people to use this app?', answer: getAnswerLabel('device_usage', formData.deviceUsage), type: 'multiple_choice' },
                { questionId: 'app_audience', question: 'Who is this app for?', answer: getAnswerLabel('app_audience', formData.appAudience), type: 'single_choice' },
                { questionId: 'auth_method', question: 'How should people log in or access the app?', answer: getAnswerLabel('auth_method', formData.authMethod), type: 'single_choice' },
                { questionId: 'development_stage', question: 'What stage is your idea at?', answer: formData.developmentStage, type: 'single_choice' }
            ];

            await initializeSession(
                formData.appIdea.trim(),
                formData.selectedPlatform,
                { enableWebSearch: formData.enableWebSearch, answers }
            );

            const result = await startWorkflowAsync(
                formData.appIdea.trim(),
                formData.selectedPlatform,
                answers
            );

            // Check if queued (show queue position)
            if (result?.queued) {
                window.showToast?.(`You're #${result.position} in queue. We'll start when a slot opens.`, 'info');
            }

        } catch (error) {
            window.showToast?.('Failed to analyze your idea. Please try again.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // Define options for VisualOptionGroup
    const platformOptions = [
        {
            id: 'cursor',
            label: 'Universal Compatibility',
            description: 'Works with Cursor, Claude Code, etc.',
            icon: <Icon name="code" size="xl" className="text-[#7B61FF]" />,
            badge: 'Recommended'
        }
    ];

    const deviceOptions = [
        { id: 'mobile', label: 'Mobile App', description: 'iOS and Android', icon: <Icon name="phone" size="xl" className="text-[#7B61FF]" /> },
        { id: 'web', label: 'Web Browser', description: 'Accessible on any computer', icon: <Icon name="search" size="xl" className="text-[#7B61FF]" /> }, // Search as proxy for 'web'
        { id: 'both', label: 'Both', description: 'Cross-platform experience', icon: <Icon name="check" size="xl" className="text-[#7B61FF]" /> }
    ];

    const audienceOptions = [
        { id: 'public', label: 'Public', description: 'Anyone can sign up', icon: <Icon name="user" size="xl" className="text-[#7B61FF]" /> },
        { id: 'company', label: 'Internal', description: 'For employees/teams', icon: <Icon name="shield" size="xl" className="text-[#7B61FF]" /> },
        { id: 'private', label: 'Private', description: 'Invite-only access', icon: <Icon name="lock" size="xl" className="text-[#7B61FF]" /> }
    ];

    const authOptions = [
        { id: 'email-password', label: 'Email & Password', description: 'Standard login', icon: <Icon name="document" size="xl" className="text-[#7B61FF]" /> },
        { id: 'social', label: 'Social Login', description: 'Google, Facebook, etc.', icon: <Icon name="heart" size="xl" className="text-[#7B61FF]" /> },
        { id: 'otp-sms', label: 'OTP / SMS', description: 'Phone verification', icon: <Icon name="phone" size="xl" className="text-[#7B61FF]" /> },
        { id: 'no-login', label: 'No Login', description: 'Open access', icon: <Icon name="arrowRight" size="xl" className="text-[#7B61FF]" /> }
    ];

    // Cloudflare-specific options (orange theme + Cloudflare Access)
    const cfDeviceOptions = [
        { id: 'mobile', label: 'Mobile App', description: 'iOS and Android', icon: <Icon name="phone" size="xl" className="text-[#F6821F]" /> },
        { id: 'web', label: 'Web Browser', description: 'Accessible on any computer', icon: <Icon name="search" size="xl" className="text-[#F6821F]" /> },
        { id: 'both', label: 'Both', description: 'Cross-platform experience', icon: <Icon name="check" size="xl" className="text-[#F6821F]" /> }
    ];

    const cfAudienceOptions = [
        { id: 'public', label: 'Public', description: 'Anyone can access', icon: <Icon name="user" size="xl" className="text-[#F6821F]" /> },
        { id: 'company', label: 'Internal', description: 'Employees/teams only', icon: <Icon name="shield" size="xl" className="text-[#F6821F]" /> },
        { id: 'private', label: 'Private', description: 'Invite-only access', icon: <Icon name="lock" size="xl" className="text-[#F6821F]" /> }
    ];

    const cfAuthOptions = [
        { id: 'email-password', label: 'Email & Password', description: 'Custom JWT via Workers', icon: <Icon name="document" size="xl" className="text-[#F6821F]" /> },
        { id: 'social', label: 'Social Login', description: 'OAuth via Workers', icon: <Icon name="heart" size="xl" className="text-[#F6821F]" /> },
        { id: 'otp-sms', label: 'OTP / SMS', description: 'Phone verification', icon: <Icon name="phone" size="xl" className="text-[#F6821F]" /> },
        { id: 'no-login', label: 'No Login', description: 'Open access', icon: <Icon name="arrowRight" size="xl" className="text-[#F6821F]" /> }
    ];

    const cfTierOptions = [
        { id: 'free', label: 'Free Tier Only', description: '$0/month', icon: <Icon name="gift" size="xl" className="text-[#22C55E]" /> },
        { id: 'paid', label: 'Include Paid Services', description: 'From $5/month', icon: <Icon name="lightning" size="xl" className="text-[#F6821F]" /> }
    ];


    // 🔄 Show queue UI when user is in queue
    if (queueStatus) {
        return (
            <div className="min-h-screen bg-black text-white relative overflow-x-hidden">
                <div className="fixed inset-0 aurora-bg z-0 pointer-events-none"></div>
                <div className="relative z-50">
                    <Navigation />
                </div>
                <div className="relative z-10 flex items-center justify-center min-h-[80vh]">
                    <div className="bg-amber-900/20 border border-amber-500/30 rounded-2xl p-10 text-center backdrop-blur-sm max-w-md mx-4">
                        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-500/20 flex items-center justify-center">
                            <svg className="w-10 h-10 text-amber-400 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <Heading level={2} className="text-amber-300 mb-3">
                            You're #{queueStatus.position} in queue
                        </Heading>
                        <Text className="text-amber-200/80 mb-1 text-lg">
                            Estimated wait: ~{queueStatus.estimatedWait} minutes
                        </Text>
                        {queueStatus.waitTimeBasedOn?.sampleSize > 0 && (
                            <Text className="text-xs text-amber-200/40 mb-2">
                                Based on {queueStatus.waitTimeBasedOn.sampleSize} recent workflows
                            </Text>
                        )}

                        {/* 🎯 TPM TRACKING: Show real-time API capacity */}
                        {queueStatus.tpm && (
                            <div className="mt-4 mb-4 p-3 bg-amber-900/30 rounded-lg">
                                <div className="flex justify-between items-center mb-2">
                                    <Text className="text-xs text-amber-200/60">API Capacity</Text>
                                    <Text className="text-xs text-amber-200/40">
                                        {queueStatus.tpm.dataSource === 'api_response' ? 'Live' : 'Estimated'}
                                    </Text>
                                </div>
                                <div className="h-2 bg-amber-900/50 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-500"
                                        style={{ width: `${queueStatus.tpm.outputTokens?.percentAvailable || 0}%` }}
                                    />
                                </div>
                                <Text className="text-xs text-amber-200/40 mt-1 text-center">
                                    {queueStatus.tpm.outputTokens?.percentAvailable || 0}% tokens available
                                </Text>
                            </div>
                        )}

                        <Text className="text-sm text-amber-200/60 mb-8">
                            We'll start automatically when a slot opens.<br />
                            Keep this page open to see progress.
                        </Text>
                        <Button
                            onClick={cancelQueue}
                            className="px-8 py-3 border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 rounded-xl"
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // Show full-page loader when workflow is active (but NOT for deployment)
    // Check both local state AND context state to handle navigation timing
    // (context may have active workflow before local sync completes)
    // Deployment has its own screen - don't show EnhancedAnalysisLoader
    if ((isWorkflowMode || workflowStatus.isActive) && !workflowStatus.isDeploymentMode) {
        // Use real-time data from context for immediate updates
        const realTimeProgress = workflowStatus.progress;
        const realTimeStep = workflowStatus.currentStep;
        const realTimeStepName = workflowStatus.stepName;
        const realTimeBatch = workflowStatus.batchProgress;
        const workflowStartedAt = workflowStatus.startedAt;

        return (
            <div className="min-h-screen bg-black text-white relative overflow-x-hidden">
                {/* Keep Navigation visible */}
                <div className="relative z-50">
                    <Navigation />
                </div>
                <EnhancedAnalysisLoader
                    currentStep={realTimeStep || currentStep}
                    loadingMessage={realTimeProgress?.message || loadingMessage || 'Processing...'}
                    platform={selectedPath === 'deploy' ? 'cloudflare' : (formData.selectedPlatform || 'cursor')}
                    originalIdea={formData.appIdea || ''}
                    isLoading={true}
                    workflowMode={true}
                    workflowProgress={realTimeProgress}
                    workflowStep={realTimeStepName}
                    batchProgress={realTimeBatch}
                    browserSafe={true}
                    startedAt={workflowStartedAt}
                    onCancel={cancelWorkflow}
                />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black text-white selection:bg-[#7B61FF] selection:text-white relative overflow-x-hidden">
            {/* Aurora Background */}
            <div className="fixed inset-0 aurora-bg z-0 pointer-events-none"></div>

            {/* Ambient Orbs */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-[#7B61FF]/20 rounded-full blur-[100px] animate-pulse-slow"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-[#9F7AEA]/20 rounded-full blur-[100px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
            </div>

            {/* Content Wrapper */}
            <div className="relative z-10 flex flex-col min-h-screen">
                <Navigation />

                <main className="flex-1 w-full max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-12">

                    {/* Hero Section */}
                    <div className="text-center mb-20 relative z-10">
                        {isReallyAuthenticated && user && (
                            <div className="mb-4 animate-fade-in">
                                <span className="text-lg font-medium bg-clip-text text-transparent bg-gradient-to-r from-gray-200 to-gray-400">
                                    Welcome back, {user.displayName.split(' ')[0]}
                                </span>
                            </div>
                        )}

                        <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/50">
                            Build Your Vision.
                            <br />
                            <span className="text-[#7B61FF]">Without The Noise.</span>
                        </h1>
                        <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
                            Describe your app, choose your stack, and let our AI architect generated
                            production-ready blueprints in minutes.
                        </p>
                    </div>

                    {/* Main Creation Form */}
                    <form onSubmit={handleSubmit} className="max-w-5xl mx-auto space-y-20 animate-fade-in relative">

                        {/* 1. The Vision */}
                        <section className="space-y-6 relative">
                            {/* Connector Line */}
                            <div className="step-connector" style={{ top: '3rem', bottom: '-4rem' }}></div>

                            <div className="flex items-center gap-4 mb-8 relative z-10">
                                <div className="w-12 h-12 rounded-full bg-[#1A1A1A] border border-[#333] flex items-center justify-center text-xl font-bold text-[#7B61FF] shadow-lg shadow-[#7B61FF]/20">1</div>
                                <h2 className="text-3xl font-bold">The Vision</h2>
                            </div>
                            <div className="glass-panel rounded-2xl p-1 ml-16">
                                <Textarea
                                    name="appIdea"
                                    value={formData.appIdea}
                                    onChange={handleInputChange}
                                    placeholder="I want to build a marketplace for verified vintage watches where AI authenticates the images..."
                                    className="w-full bg-transparent border-none text-xl p-8 min-h-[200px] focus:ring-0 placeholder-gray-500 resize-none font-light leading-relaxed"
                                    style={{ background: 'transparent' }} // Force transparent
                                />
                            </div>
                        </section>

                        {/* 2. Choose Your Path */}
                        <section className="space-y-6 relative">
                            {/* Connector Line */}
                            <div className="step-connector" style={{ top: '3rem', bottom: '-4rem' }}></div>

                            <div className="flex items-center gap-4 mb-8 relative z-10">
                                <div className={`w-12 h-12 rounded-full bg-[#1A1A1A] border flex items-center justify-center text-xl font-bold transition-all duration-300 ${
                                    selectedPath === 'deploy'
                                        ? 'border-[#F6821F]/50 text-[#F6821F] shadow-lg shadow-[#F6821F]/20'
                                        : 'border-[#333] text-[#7B61FF] shadow-lg shadow-[#7B61FF]/20'
                                }`}>2</div>
                                <h2 className="text-3xl font-bold">Choose Your Path</h2>
                            </div>

                            <div className="ml-16">
                                <PathSelector
                                    value={selectedPath}
                                    onChange={setSelectedPath}
                                    disabled={isLoading}
                                    isAuthenticated={isReallyAuthenticated}
                                />
                            </div>
                        </section>

                        {/* 3. Path-specific content - Only show after authentication */}
                        {isReallyAuthenticated && (
                            selectedPath === 'vibe' ? (
                                /* Vibe Coding Path: Simple description + Start */
                                <section className="space-y-6 relative">
                                    {/* Connector Line */}
                                    <div className="step-connector" style={{ top: '3rem', bottom: '-4rem' }}></div>

                                    <div className="flex items-center gap-4 mb-8 relative z-10">
                                        <div className="w-12 h-12 rounded-full bg-[#1A1A1A] border border-[#06B6D4]/50 flex items-center justify-center text-xl font-bold text-[#06B6D4] shadow-lg shadow-[#06B6D4]/20">3</div>
                                        <h2 className="text-3xl font-bold">How It Works</h2>
                                    </div>

                                    <div className="ml-16 space-y-6">
                                        <div className="glass-panel p-6 rounded-xl border border-[#06B6D4]/20 bg-gradient-to-br from-[#06B6D4]/5 to-transparent">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                <div className="flex flex-col items-center text-center p-4">
                                                    <div className="w-12 h-12 rounded-xl bg-[#06B6D4]/20 flex items-center justify-center mb-3">
                                                        <Icon name="document" size="lg" className="text-[#06B6D4]" />
                                                    </div>
                                                    <h4 className="font-semibold text-white mb-1">1. Describe</h4>
                                                    <p className="text-sm text-gray-400">Tell the AI what you want to build</p>
                                                </div>
                                                <div className="flex flex-col items-center text-center p-4">
                                                    <div className="w-12 h-12 rounded-xl bg-[#06B6D4]/20 flex items-center justify-center mb-3">
                                                        <Icon name="code" size="lg" className="text-[#06B6D4]" />
                                                    </div>
                                                    <h4 className="font-semibold text-white mb-1">2. Iterate</h4>
                                                    <p className="text-sm text-gray-400">Watch it build live, request changes</p>
                                                </div>
                                                <div className="flex flex-col items-center text-center p-4">
                                                    <div className="w-12 h-12 rounded-xl bg-[#06B6D4]/20 flex items-center justify-center mb-3">
                                                        <Icon name="lightning" size="lg" className="text-[#06B6D4]" />
                                                    </div>
                                                    <h4 className="font-semibold text-white mb-1">3. Deploy</h4>
                                                    <p className="text-sm text-gray-400">Launch to Cloudflare when ready</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="p-4 rounded-xl bg-[#06B6D4]/10 border border-[#06B6D4]/30">
                                            <div className="flex items-start gap-3">
                                                <Icon name="check" size="md" className="text-[#06B6D4] mt-0.5" />
                                                <div>
                                                    <p className="text-sm text-gray-300">
                                                        <span className="font-semibold text-white">Real-time coding:</span> Your app updates instantly as you chat with the AI. No waiting for full regeneration.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            ) : selectedPath === 'blueprint' ? (
                                /* Architect Path: Show questions */
                                <section className="space-y-6 relative">
                                    {/* Connector Line */}
                                    <div className="step-connector" style={{ top: '3rem', bottom: '-4rem' }}></div>

                                    <div className="flex items-center gap-4 mb-8 relative z-10">
                                        <div className="w-12 h-12 rounded-full bg-[#1A1A1A] border border-[#333] flex items-center justify-center text-xl font-bold text-[#7B61FF] shadow-lg shadow-[#7B61FF]/20">3</div>
                                        <h2 className="text-3xl font-bold">The Specifics</h2>
                                    </div>

                                    <div className="ml-16 space-y-8">
                                        {/* Row 1: Target Device */}
                                        <div className="space-y-4">
                                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-widest pl-1">Target Device</h3>
                                            <VisualOptionGroup
                                                options={deviceOptions}
                                                value={formData.deviceUsage}
                                                onChange={(val) => handleOptionChange('deviceUsage', val)}
                                                multi={true}
                                                columns={3}
                                            />
                                        </div>

                                        {/* Row 2: Target Audience */}
                                        <div className="space-y-4">
                                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-widest pl-1">Target Audience</h3>
                                            <VisualOptionGroup
                                                options={audienceOptions}
                                                value={formData.appAudience}
                                                onChange={(val) => handleOptionChange('appAudience', val)}
                                                columns={3}
                                            />
                                        </div>

                                        {/* Row 3: Authentication */}
                                        <div className="space-y-4">
                                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-widest pl-1">Authentication</h3>
                                            <VisualOptionGroup
                                                options={authOptions}
                                                value={formData.authMethod}
                                                onChange={(val) => handleOptionChange('authMethod', val)}
                                                columns={4}
                                            />
                                        </div>
                                    </div>
                                </section>
                            ) : (
                                /* Deploy Path: Questions + Generate architecture */
                                <>
                                    {/* Step 3: The Specifics (Questions) - Cloudflare Orange Theme */}
                                    <section className="space-y-6 relative">
                                        {/* Connector Line */}
                                        <div className="step-connector" style={{ top: '3rem', bottom: '-4rem' }}></div>

                                        <div className="flex items-center gap-4 mb-8 relative z-10">
                                            <div className="w-12 h-12 rounded-full bg-[#1A1A1A] border border-[#F6821F]/50 flex items-center justify-center text-xl font-bold text-[#F6821F] shadow-lg shadow-[#F6821F]/20">3</div>
                                            <h2 className="text-3xl font-bold">The Specifics</h2>
                                            <img src="https://www.cloudflare.com/favicon.ico" alt="Cloudflare" className="w-6 h-6 ml-2 opacity-60" />
                                        </div>

                                        <div className="ml-16 space-y-8 p-6 rounded-2xl border border-[#F6821F]/30">
                                            {/* Row 1: Target Device */}
                                            <div className="space-y-4">
                                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-widest pl-1">Target Device</h3>
                                                <VisualOptionGroup
                                                    options={cfDeviceOptions}
                                                    value={formData.deviceUsage}
                                                    onChange={(val) => handleOptionChange('deviceUsage', val)}
                                                    multi={true}
                                                    columns={3}
                                                    accentColor="orange"
                                                />
                                            </div>

                                            {/* Row 2: Target Audience */}
                                            <div className="space-y-4">
                                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-widest pl-1">Target Audience</h3>
                                                <VisualOptionGroup
                                                    options={cfAudienceOptions}
                                                    value={formData.appAudience}
                                                    onChange={(val) => handleOptionChange('appAudience', val)}
                                                    columns={3}
                                                    accentColor="orange"
                                                />
                                            </div>

                                            {/* Row 3: Authentication */}
                                            <div className="space-y-4">
                                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-widest pl-1">Authentication</h3>
                                                {(formData.appAudience === 'company' || formData.appAudience === 'private') ? (
                                                    /* Cloudflare Access is default for internal/private apps */
                                                    <div className="p-4 rounded-xl bg-[#F6821F]/10 border border-[#F6821F]/30">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-12 h-12 rounded-xl bg-[#F6821F]/20 flex items-center justify-center">
                                                                <Icon name="shield" size="lg" className="text-[#F6821F]" />
                                                            </div>
                                                            <div>
                                                                <p className="font-semibold text-white">Cloudflare Access (Zero Trust)</p>
                                                                <p className="text-sm text-gray-400">Auto-selected for {formData.appAudience === 'company' ? 'internal' : 'private'} apps</p>
                                                            </div>
                                                            <div className="ml-auto">
                                                                <span className="px-3 py-1 text-xs font-medium bg-[#F6821F]/20 text-[#F6821F] rounded-full">
                                                                    50 users free
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <p className="mt-3 text-xs text-gray-500">
                                                            🔐 Cloudflare Access provides enterprise-grade SSO protection. Users authenticate via your identity provider (Google, Okta, etc.) before accessing your app.
                                                            <a href="https://developers.cloudflare.com/cloudflare-one/policies/access/" target="_blank" rel="noopener noreferrer" className="ml-1 text-[#F6821F] hover:underline">
                                                                Learn more →
                                                            </a>
                                                        </p>
                                                    </div>
                                                ) : (
                                                    /* Show auth options for public apps */
                                                    <VisualOptionGroup
                                                        options={cfAuthOptions}
                                                        value={formData.authMethod}
                                                        onChange={(val) => handleOptionChange('authMethod', val)}
                                                        columns={4}
                                                        accentColor="orange"
                                                    />
                                                )}
                                            </div>

                                            {/* Row 4: Service Tier */}
                                            <div className="space-y-4">
                                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-widest pl-1">Service Tier</h3>
                                                <VisualOptionGroup
                                                    options={cfTierOptions}
                                                    value={formData.cfTier}
                                                    onChange={(val) => handleOptionChange('cfTier', val)}
                                                    columns={2}
                                                    accentColor="orange"
                                                />
                                                {/* Info box showing what's included/excluded */}
                                                <div className="p-4 rounded-xl bg-black/30 border border-white/10 space-y-3">
                                                    {formData.cfTier === 'free' ? (
                                                        <>
                                                            <div className="flex items-center gap-2 text-green-400 text-sm font-medium">
                                                                <Icon name="check" size="sm" />
                                                                <span>Free tier includes everything you need to launch:</span>
                                                            </div>
                                                            <p className="text-xs text-gray-400 pl-6">
                                                                Workers (100K req/day), Pages (500 builds/mo), D1 (5GB), KV (1GB), R2 (10GB),
                                                                Queues (1M msg/mo), Cron, Workers AI, AI Gateway, Vectorize, Access (50 users)
                                                            </p>
                                                            <div className="flex items-center gap-2 text-amber-400/80 text-sm mt-2">
                                                                <Icon name="lightning" size="sm" />
                                                                <span>Paid services excluded:</span>
                                                            </div>
                                                            <p className="text-xs text-gray-500 pl-6">
                                                                Durable Objects, Hyperdrive, Browser Rendering, Stream, Images
                                                            </p>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="flex items-center gap-2 text-[#F6821F] text-sm font-medium">
                                                                <Icon name="lightning" size="sm" />
                                                                <span>All services available based on your app needs:</span>
                                                            </div>
                                                            <p className="text-xs text-gray-400 pl-6">
                                                                Everything in free tier + Durable Objects (real-time/WebSockets),
                                                                Hyperdrive (external DB pooling), Browser Rendering (screenshots/PDFs),
                                                                Stream (video), Images (optimization)
                                                            </p>
                                                            <div className="flex items-center justify-between mt-2">
                                                                <div className="flex items-center gap-2 text-amber-400/80 text-sm">
                                                                    <Icon name="document" size="sm" />
                                                                    <span>Requires Workers Paid plan ($5/month)</span>
                                                                </div>
                                                                <a
                                                                    href="https://www.cloudflare.com/plans/developer-platform/"
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-xs text-[#F6821F] hover:underline"
                                                                >
                                                                    View pricing →
                                                                </a>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </section>

                                    {/* Step 4: Generate Architecture OR Show Results + Connect */}
                                    <section className="space-y-6 relative">
                                        {/* Connector Line */}
                                        <div className="step-connector" style={{ top: '3rem', bottom: '-4rem' }}></div>

                                        {!showAnalysisResults ? (
                                            /* Before analysis: Show info */
                                            <>
                                                <div className="flex items-center gap-4 mb-8 relative z-10">
                                                    <div className="w-12 h-12 rounded-full bg-[#1A1A1A] border border-[#333] flex items-center justify-center text-xl font-bold text-[#F6821F] shadow-lg shadow-[#F6821F]/20">4</div>
                                                    <h2 className="text-3xl font-bold">Generate Architecture</h2>
                                                </div>

                                                <div className="ml-16">
                                                    <div className="glass-panel p-6 rounded-xl border border-[#F6821F]/20 bg-gradient-to-br from-[#F6821F]/5 to-transparent">
                                                        <div className="flex items-start gap-4">
                                                            <div className="w-10 h-10 rounded-lg bg-[#F6821F]/20 flex items-center justify-center flex-shrink-0">
                                                                <Icon name="lightning" size="md" className="text-[#F6821F]" />
                                                            </div>
                                                            <div>
                                                                <h4 className="font-semibold text-white mb-2">What you'll get:</h4>
                                                                <ul className="text-sm text-gray-400 space-y-1">
                                                                    <li>• Complete Cloudflare architecture (Workers, Pages, D1, KV, R2)</li>
                                                                    <li>• AI service recommendations (Workers AI, AI Gateway, Vectorize)</li>
                                                                    {(formData.appAudience === 'company' || formData.appAudience === 'private') && (
                                                                        <li>• Cloudflare Access for Zero Trust protection</li>
                                                                    )}
                                                                    <li>• Free tier optimized - $0/month to start</li>
                                                                    <li>• Implementation prompts for your AI coding assistant</li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                        /* After analysis: Show connect infrastructure */
                                        <>
                                            <div className="flex items-center gap-4 mb-8 relative z-10">
                                                <div className="w-12 h-12 rounded-full bg-[#1A1A1A] border border-[#333] flex items-center justify-center text-xl font-bold text-[#22C55E] shadow-lg shadow-[#22C55E]/20">
                                                    <Icon name="check" size="md" className="text-[#22C55E]" />
                                                </div>
                                                <h2 className="text-3xl font-bold">Architecture Ready</h2>
                                            </div>

                                            <div className="ml-16 space-y-6">
                                                <p className="text-gray-400">
                                                    Your Cloudflare architecture is ready! Connect your accounts to deploy.
                                                </p>
                                                <CloudflareDeployWizard
                                                    onStatusChange={setInfraStatus}
                                                    disabled={isLoading}
                                                    sessionId={sessionId}
                                                    architectureData={v2Data}
                                                />
                                            </div>
                                        </>
                                    )}
                                    </section>
                                </>
                            )
                        )}

                        {/* 4. Action Area */}
                        <section className="sticky bottom-8 z-50">
                            {isReallyAuthenticated ? (
                                selectedPath === 'vibe' ? (
                                    /* Vibe Coding Action Bar */
                                    <div className="glass-panel p-4 rounded-2xl flex items-center justify-between gap-6 max-w-3xl mx-auto shadow-2xl bg-[#050505]/80 backdrop-blur-xl border border-[#06B6D4]/30">
                                        <div className="hidden md:block pl-4">
                                            <div className="text-sm font-medium text-[#06B6D4]">Ready to vibe?</div>
                                            <div className="text-xs text-gray-600">Start coding in real-time</div>
                                        </div>

                                        <div className="hidden md:flex items-center gap-3">
                                            <CreditTracker show={true} compact={true} />
                                        </div>

                                        <Button
                                            type="button"
                                            disabled={isLoading || !formData.appIdea.trim()}
                                            onClick={async () => {
                                                if (!formData.appIdea.trim()) {
                                                    window.showToast?.('Please describe what you want to build', 'error');
                                                    return;
                                                }
                                                setIsLoading(true);
                                                try {
                                                    // Create a simple session for vibe coding
                                                    // force: true ensures new session - don't reuse old vibe sessions
                                                    const newSessionId = await initializeSession(formData.appIdea.trim(), 'vibe', { enableWebSearch: false, answers: [], force: true });
                                                    // Navigate to vibe coding page with the returned session ID
                                                    navigate(`/vibe/${newSessionId}`);
                                                } catch (error) {
                                                    window.showToast?.('Failed to start vibe coding. Please try again.', 'error');
                                                } finally {
                                                    setIsLoading(false);
                                                }
                                            }}
                                            className={`
                                                flex-1 md:flex-none px-12 py-4 text-lg rounded-xl font-bold transition-all
                                                ${formData.appIdea.trim()
                                                    ? 'bg-gradient-to-r from-[#06B6D4] to-[#22D3EE] hover:opacity-90 shadow-[0_0_30px_rgba(6,182,212,0.4)]'
                                                    : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                                                }
                                            `}
                                        >
                                            {isLoading ? 'Starting...' : (
                                                <span className="flex items-center gap-2">
                                                    <Icon name="code" size="sm" />
                                                    Start Vibe Coding
                                                </span>
                                            )}
                                        </Button>
                                    </div>
                                ) : selectedPath === 'blueprint' ? (
                                    /* Blueprint Action Bar */
                                    <div className="glass-panel p-4 rounded-2xl flex items-center justify-between gap-6 max-w-3xl mx-auto shadow-2xl bg-[#050505]/80 backdrop-blur-xl border border-white/10">
                                        <div className="hidden md:block pl-4">
                                            <div className="text-sm font-medium text-gray-400">Ready to architect?</div>
                                            <div className="text-xs text-gray-600">Takes ~8-10 minutes</div>
                                        </div>

                                        <div className="hidden md:flex items-center gap-3">
                                            <QueueIndicator />
                                            <CreditTracker show={true} compact={true} />
                                        </div>

                                        <Button
                                            type="submit"
                                            disabled={isLoading}
                                            className="flex-1 md:flex-none px-12 py-4 text-lg bg-gradient-to-r from-[#7B61FF] to-[#BA87FF] hover:opacity-90 transition-opacity rounded-xl font-bold shadow-[0_0_30px_rgba(123,97,255,0.4)]"
                                        >
                                            {isLoading ? 'Architecting...' : "Let's Go"}
                                        </Button>
                                    </div>
                                ) : (
                                    /* Deploy Action Bar - changes based on analysis state */
                                    <div className="glass-panel p-4 rounded-2xl flex items-center justify-between gap-6 max-w-3xl mx-auto shadow-2xl bg-[#050505]/80 backdrop-blur-xl border border-white/10">
                                        <div className="hidden md:block pl-4">
                                            {!showAnalysisResults ? (
                                                /* Before analysis */
                                                <>
                                                    <div className="text-sm font-medium text-gray-400">Ready to architect?</div>
                                                    <div className="text-xs text-gray-600">Takes ~8-10 minutes</div>
                                                </>
                                            ) : infraStatus.ready ? (
                                                /* After analysis + connected */
                                                <>
                                                    <div className="text-sm font-medium text-green-400">Infrastructure connected</div>
                                                    <div className="text-xs text-gray-600">Ready to build & deploy</div>
                                                </>
                                            ) : (
                                                /* After analysis, not connected */
                                                <>
                                                    <div className="text-sm font-medium text-[#F6821F]">Architecture ready!</div>
                                                    <div className="text-xs text-gray-600">Connect GitHub + Cloudflare to deploy</div>
                                                </>
                                            )}
                                        </div>

                                        <div className="hidden md:flex items-center gap-3">
                                            {!showAnalysisResults && <QueueIndicator />}
                                            <CreditTracker show={true} compact={true} />
                                        </div>

                                        {!showAnalysisResults ? (
                                            /* Generate Architecture Button */
                                            <Button
                                                type="button"
                                                disabled={!formData.appIdea.trim() || isLoading}
                                                onClick={async () => {
                                                    // Trigger analysis with cloudflare platform
                                                    if (!formData.appIdea.trim()) {
                                                        window.showToast?.('Please enter your app idea', 'error');
                                                        return;
                                                    }
                                                    setIsLoading(true);
                                                    try {
                                                        // Auto-select cloudflare-access for internal/private apps
                                                        const authMethod = (formData.appAudience === 'company' || formData.appAudience === 'private')
                                                            ? 'cloudflare-access'
                                                            : formData.authMethod;

                                                        const answers = [
                                                            { questionId: 'device_usage', question: 'Where do you want people to use this app?', answer: formData.deviceUsage.join(', '), type: 'multiple_choice' },
                                                            { questionId: 'app_audience', question: 'Who is this app for?', answer: formData.appAudience, type: 'single_choice' },
                                                            { questionId: 'auth_method', question: 'How should people log in?', answer: authMethod, type: 'single_choice' },
                                                            { questionId: 'development_stage', question: 'What stage is your idea at?', answer: formData.developmentStage, type: 'single_choice' },
                                                            { questionId: 'cf_tier', question: 'Service tier preference?', answer: formData.cfTier, type: 'single_choice' }
                                                        ];
                                                        await initializeSession(formData.appIdea.trim(), 'cloudflare', { enableWebSearch: formData.enableWebSearch, answers });
                                                        const result = await startWorkflowAsync(formData.appIdea.trim(), 'cloudflare', answers);
                                                        if (result?.queued) {
                                                            window.showToast?.(`You're #${result.position} in queue.`, 'info');
                                                        }
                                                    } catch (error) {
                                                        window.showToast?.('Failed to start analysis. Please try again.', 'error');
                                                    } finally {
                                                        setIsLoading(false);
                                                    }
                                                }}
                                                className={`
                                                    flex-1 md:flex-none px-12 py-4 text-lg rounded-xl font-bold transition-all
                                                    ${formData.appIdea.trim()
                                                        ? 'bg-gradient-to-r from-[#F6821F] to-[#FAAD3F] hover:opacity-90 shadow-[0_0_30px_rgba(246,130,31,0.4)]'
                                                        : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                                                    }
                                                `}
                                            >
                                                {isLoading ? 'Generating...' : (
                                                    <span className="flex items-center gap-2">
                                                        <Icon name="lightning" size="sm" />
                                                        Generate Architecture
                                                    </span>
                                                )}
                                            </Button>
                                        ) : (
                                            /* Deploy Button (after architecture generated) */
                                            <Button
                                                type="button"
                                                disabled={!infraStatus.ready || isLoading}
                                                onClick={() => {
                                                    // TODO: Implement actual deploy flow
                                                    window.showToast?.('Deploy feature coming soon!', 'info');
                                                }}
                                                className={`
                                                    flex-1 md:flex-none px-12 py-4 text-lg rounded-xl font-bold transition-all
                                                    ${infraStatus.ready
                                                        ? 'bg-gradient-to-r from-[#F6821F] to-[#FAAD3F] hover:opacity-90 shadow-[0_0_30px_rgba(246,130,31,0.4)]'
                                                        : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                                                    }
                                                `}
                                            >
                                                {isLoading ? 'Deploying...' : (
                                                    <span className="flex items-center gap-2">
                                                        <Icon name="lightning" size="sm" />
                                                        Deploy Now
                                                    </span>
                                                )}
                                            </Button>
                                        )}
                                    </div>
                                )
                            ) : (
                                <div className="glass-panel p-6 rounded-2xl max-w-3xl mx-auto shadow-2xl bg-[#050505]/80 backdrop-blur-xl border border-[#7B61FF]/30">
                                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-full bg-[#7B61FF]/20 flex items-center justify-center flex-shrink-0">
                                                <Icon name="lock" size="md" className="text-[#7B61FF]" />
                                            </div>
                                            <div className="text-center md:text-left">
                                                <div className="text-lg font-semibold text-white">Sign in to {selectedPath === 'blueprint' ? 'generate architecture' : selectedPath === 'vibe' ? 'start vibe coding' : 'deploy your app'}</div>
                                                <div className="text-sm text-gray-400">Free to start - no credit card required</div>
                                            </div>
                                        </div>
                                        <Button
                                            type="button"
                                            onClick={() => navigate('/signin')}
                                            className="w-full md:w-auto px-8 py-3 text-base bg-gradient-to-r from-[#7B61FF] to-[#BA87FF] hover:opacity-90 transition-opacity rounded-xl font-semibold shadow-[0_0_30px_rgba(123,97,255,0.4)]"
                                        >
                                            Sign In with Google
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </section>
                    </form>
                </main>

                <Footer />
            </div>
        </div>
    );
}